import { Injectable } from '@angular/core';
import { Route, Router, Routes } from '@angular/router';
import { environment } from 'src/environments/environment';

const shipmentApiUrlModeStorageKey = 'shipment-api-url-mode';
const showPgSwitchStorageKey = 'showPgSwitch';
type ShipmentApiUrlMode = 'default' | 'pgsql';

@Injectable({
  providedIn: 'root'
})
export class ShipmentApiUrlService {
  private readonly defaultUrl = environment.shipmentApiUrl;
  private readonly alternateUrl = environment.alternateShipmentApiUrl;
  private currentUrl = this.resolveInitialUrl();

  public isSwitchEnabled(): boolean {
    return environment.enableShipmentApiSwitch && !!this.alternateUrl && this.isSwitchUnlockedForUser();
  }

  private isSwitchUnlockedForUser(): boolean {
    return localStorage.getItem(showPgSwitchStorageKey) === 'true';
  }

  public getCurrentUrl(): string {
    return this.currentUrl;
  }

  public isAlternateSelected(): boolean {
    return this.currentUrl === this.alternateUrl;
  }

  public getCurrentLabel(): string {
    return this.isAlternateSelected() ? 'PostgreSQL' : 'SQL Server';
  }

  public setAlternateSelected(useAlternate: boolean): void {
    if (!this.isSwitchEnabled()) {
      return;
    }

    this.currentUrl = useAlternate ? this.alternateUrl : this.defaultUrl;
    localStorage.setItem(shipmentApiUrlModeStorageKey, useAlternate ? 'pgsql' : 'default');
  }

  public applyRuntimeConfiguration(router: Router): void {
    if (!this.isSwitchEnabled()) {
      localStorage.removeItem(shipmentApiUrlModeStorageKey);
    } else {
      this.currentUrl = this.resolveInitialUrl();
    }

    const shipmentRoute = this.findRouteByPath(router.config, 'shipment');
    if (shipmentRoute == null) {
      return;
    }

    shipmentRoute.data = {
      ...(shipmentRoute.data || {}),
      shipmentApiUrl: this.currentUrl
    };
  }

  private resolveInitialUrl(): string {
    if (!this.isSwitchEnabled()) {
      return this.defaultUrl;
    }

    return this.getStoredMode() === 'pgsql' ? this.alternateUrl : this.defaultUrl;
  }

  private getStoredMode(): ShipmentApiUrlMode {
    const storedMode = localStorage.getItem(shipmentApiUrlModeStorageKey);
    return storedMode === 'pgsql' ? 'pgsql' : 'default';
  }

  private findRouteByPath(routes: Routes, path: string): Route | undefined {
    for (const route of routes) {
      if (route.path === path) {
        return route;
      }

      if (route.children) {
        const nestedRoute = this.findRouteByPath(route.children, path);
        if (nestedRoute) {
          return nestedRoute;
        }
      }
    }

    return undefined;
  }
}
